import { motion } from "framer-motion";
import { Printer, Download } from "lucide-react";

const treatments = [
  {
    category: "Swallowing",
    items: [
      {
        name: "Comprehensive Swallow Evaluation",
        desc: "Clinical bedside assessment with referral pathways for instrumental study.",
      },
      {
        name: "Dysphagia Therapy",
        desc: "Targeted exercises, diet modification and compensatory strategies.",
      },
      {
        name: "Mealtime Coaching",
        desc: "Caregiver and family training for safe oral intake at home.",
      },
    ],
  },
  {
    category: "Motor Speech",
    items: [
      {
        name: "Dysarthria Treatment",
        desc: "Improving speech intelligibility, breath support and articulation.",
      },
      {
        name: "Apraxia of Speech Therapy",
        desc: "Programmes to rebuild motor planning for speech production.",
      },
      {
        name: "SPEAK OUT!®",
        desc: "Evidence-based voice treatment for Parkinson's disease (Parkinson Voice Project) — speaking with intent.",
      },
    ],
  },
  {
    category: "Language & Cognition",
    items: [
      {
        name: "Aphasia Therapy",
        desc: "Restoring expressive and receptive language after stroke or brain injury.",
      },
      {
        name: "Cognitive-Communication Therapy",
        desc: "Memory, attention, problem-solving and executive function support.",
      },
      {
        name: "Reading & Writing Rehabilitation",
        desc: "Functional literacy goals for return to work, study and daily life.",
      },
    ],
  },
  {
    category: "Voice & Resonance",
    items: [
      {
        name: "Voice Therapy",
        desc: "Behavioural treatment for vocal quality, pitch and stamina.",
      },
      {
        name: "Vocal Hygiene Counseling",
        desc: "Lifestyle and behavioural strategies to protect the voice long-term.",
      },
      {
        name: "Accent & Communication Coaching",
        desc: "Clarity, confidence and presence-based communication training.",
      },
    ],
  },
];

export const Treatments = () => {
  return (
    <section
      id="treatments"
      data-testid="treatments-section"
      className="relative py-20 md:py-32 bg-[#F3F1E7]"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-16">
          <div className="lg:col-span-5">
            <p className="text-xs uppercase tracking-[0.25em] text-[#5C6661] mb-4">
              Adult Treatment Catalogue
            </p>
            <h2
              data-testid="treatments-title"
              className="font-serif text-3xl md:text-4xl lg:text-5xl font-light tracking-tight text-[#2D332F] leading-tight"
            >
              A complete continuum of adult therapy services.
            </h2>
          </div>
          <div className="lg:col-span-6 lg:col-start-7 self-end space-y-4">
            <p className="text-[#5C6661] text-lg leading-relaxed">
              From the first swallow evaluation to long-term voice maintenance —
              every service follows current best-practice and is tailored to
              measurable, meaningful goals.
            </p>
            <div className="no-print flex flex-wrap gap-3">
              <button
                type="button"
                onClick={() => window.print()}
                data-testid="treatments-print"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-[#2D332F]/20 text-sm text-[#2D332F] hover:bg-[#2D332F] hover:text-[#FDFCF8] transition"
              >
                <Printer size={14} strokeWidth={1.5} />
                Print this list
              </button>
              <a
                href={`${process.env.REACT_APP_BACKEND_URL}/api/treatments.pdf`}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="treatments-pdf"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#739072] text-white text-sm hover:bg-[#5C765B] transition"
              >
                <Download size={14} strokeWidth={1.5} />
                Download PDF
              </a>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 md:gap-x-16 md:gap-y-20">
          {treatments.map((group, gi) => (
            <motion.div
              key={group.category}
              data-testid={`treatment-group-${group.category.toLowerCase().replace(/\s|&/g, "-")}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, delay: gi * 0.08 }}
            >
              <div className="flex items-baseline gap-4 mb-8">
                <span className="font-serif text-xl text-[#739072]">
                  {String(gi + 1).padStart(2, "0")}
                </span>
                <h3 className="font-serif text-2xl md:text-3xl font-medium tracking-tight text-[#2D332F]">
                  {group.category}
                </h3>
              </div>
              <ul className="space-y-1">
                {group.items.map((it) => (
                  <li
                    key={it.name}
                    data-testid={`treatment-item-${it.name.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    className="treatment-row py-5 -mx-3 px-3 rounded-2xl border-t border-[#2D332F]/10"
                  >
                    <p className="font-medium text-[#2D332F] text-base md:text-lg">
                      {it.name}
                    </p>
                    <p className="text-sm text-[#5C6661] mt-1.5 leading-relaxed">
                      {it.desc}
                    </p>
                  </li>
                ))}
              </ul>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
